/**
 * Normalize a value before it reaches Sentry so an event can never carry a
 * dead title like "[object Object]" or "captureException".
 *
 * `Sentry.captureException` accepts `unknown`, but the SDK serialises a
 * non-Error value with `String(value)`: a plain object becomes the useless
 * "[object Object]" title and `undefined` becomes a title built from the
 * calling function's name. Those titles hid the actual failure behind
 * unreadable cards ("[object Object]", dozens of users; "captureException")
 * that nobody could triage. This converts any value into a real Error whose
 * message carries the shape of what was captured, so a bad capture is at
 * least legible in the queue.
 *
 * Pure on purpose: testable under `node --test` without importing Sentry.
 */
export function normalizeCapturedError(value: unknown): Error {
  if (value instanceof Error) return value
  if (value === undefined || value === null) {
    return new Error('Non-Error value captured: null/undefined')
  }
  if (typeof value === 'string') return new Error(value)
  if (typeof value !== 'object') return new Error(String(value))
  // Never serialize an opaque object: SDK errors can contain request/provider
  // bodies, and truncating their JSON could bypass downstream payload redaction.
  const record = value as Record<string, unknown>
  const read = (key: string): unknown => {
    try {
      return record[key]
    } catch {
      return undefined
    }
  }
  const message = ['message', 'msg', 'error_description', 'error']
    .map(read)
    .find((item): item is string => typeof item === 'string' && item.length > 0)
  const code = [read('code'), read('error_code')].find(
    (item): item is string => typeof item === 'string' && /^[A-Z0-9_]{1,80}$/i.test(item),
  )
  const error = new Error(
    message?.slice(0, 800) ?? (code ? `Non-Error failure (${code})` : 'Non-Error object captured'),
  )
  if (code) Object.assign(error, { code })
  return error
}
